import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import CandidateCard from "@/components/CandidateCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Filter, X } from "lucide-react";

const STATUSES = ["Available", "Booked", "Interviewed", "Selected", "Processing", "Departed", "Inactive"];

export default function Catalog() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [countryId, setCountryId] = useState("all");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [minExp, setMinExp] = useState("all");

  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: async () => (await api.get("/countries")).data });
  const { data: rolesData } = useQuery({
    queryKey: ["candidate-roles"],
    queryFn: async () => (await api.get("/candidates/roles")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["candidates", q, countryId, status, minExp],
    queryFn: async () => {
      // Fetch a generous page (backend caps at MAX_CANDIDATES_LIMIT=500). Client-side
      // filtering by role still applies. Increased from 100 → 500 so agencies with
      // large rosters (e.g. after bulk import) don't silently lose candidates on the
      // public catalog / admin dashboard.
      const params = { limit: 500, exclude_in_process: true };
      if (q) params.q = q;
      if (countryId !== "all") params.country_id = countryId;
      if (status !== "all") params.status = status;
      if (minExp !== "all") params.min_exp = Number(minExp);
      return (await api.get("/candidates", { params })).data;
    },
  });

  const filtered = useMemo(() => {
    if (role === "all") return candidates;
    const target = role.toLowerCase();
    return candidates.filter((c) => {
      const primary = (c.role || "").toLowerCase() === target;
      const multi = Array.isArray(c.roles) && c.roles.some((r) => (r || "").toLowerCase() === target);
      return primary || multi;
    });
  }, [candidates, role]);

  // Full role list from backend (canonical Formal+Non-Formal + any distinct role in DB),
  // grouped by type so Formal & Non-Formal categories are visually separated.
  const formalRoles = useMemo(() => (rolesData?.formal || []).map((r) => r.value), [rolesData]);
  const nonFormalRoles = useMemo(() => (rolesData?.non_formal || []).map((r) => r.value), [rolesData]);

  const clearFilters = () => {
    setQ(""); setCountryId("all"); setStatus("all"); setRole("all"); setMinExp("all");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
      <div className="overline mb-2 sm:mb-4">{t("catalog.title", "Candidate Catalog")}</div>
      <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-zinc-950 tracking-tight max-w-4xl leading-[1.1]">{t("catalog.subtitle", "Browse rigorously vetted workers.")}</h1>

      <div className="mt-6 sm:mt-10 flex flex-col lg:flex-row lg:items-center gap-2.5 sm:gap-3 border border-zinc-200 rounded-2xl sm:rounded-3xl p-3 sm:p-4 bg-white">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("catalog.search_placeholder", "Search by name, skill, role…")}
            className="pl-11 h-11 rounded-full border-zinc-200"
            data-testid="catalog-search"
          />
        </div>

        <div className="grid grid-cols-2 lg:contents gap-2.5">
          <Select value={countryId} onValueChange={setCountryId}>
            <SelectTrigger className="h-11 rounded-full lg:w-44" data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("catalog.filter_country", "All countries")}</SelectItem>
              {countries.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>

          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-11 rounded-full lg:w-44" data-testid="filter-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("catalog.filter_role", "All Job Types")}</SelectItem>
              {nonFormalRoles.length > 0 && (
                <div className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                  {t("catalog.role_non_formal", "Non-Formal")}
                </div>
              )}
              {nonFormalRoles.map((r) => (<SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{r}</SelectItem>))}
              {formalRoles.length > 0 && (
                <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-widest text-emerald-700">
                  {t("catalog.role_formal", "Formal")}
                </div>
              )}
              {formalRoles.map((r) => (<SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{r}</SelectItem>))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 rounded-full lg:w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("catalog.filter_status", "All statuses")}</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>

          <Select value={minExp} onValueChange={setMinExp}>
            <SelectTrigger className="h-11 rounded-full lg:w-40" data-testid="filter-exp"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("catalog.filter_experience", "Min. experience")}</SelectItem>
              <SelectItem value="1">1+ {t("candidate.years", "yrs")}</SelectItem>
              <SelectItem value="3">3+ {t("candidate.years", "yrs")}</SelectItem>
              <SelectItem value="5">5+ {t("candidate.years", "yrs")}</SelectItem>
              <SelectItem value="10">10+ {t("candidate.years", "yrs")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <button className="text-xs text-zinc-800 hover:text-zinc-900 inline-flex items-center justify-center gap-1 px-3 py-2 lg:py-0" onClick={clearFilters} data-testid="clear-filters">
          <X size={12} /> {t("action.cancel", "Clear")}
        </button>
      </div>

      <div className="mt-4 sm:mt-6 text-xs sm:text-sm text-zinc-800" data-testid="catalog-count">
        <Filter size={12} className="inline mr-1 text-[#b8860b]" /> {t(filtered.length === 1 ? "catalog.count_one" : "catalog.count_other", "{{count}} candidates", { count: filtered.length })}
      </div>

      {isLoading ? (
        <div className="mt-12 text-center text-zinc-800">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-12 text-center text-zinc-800" data-testid="catalog-empty">{t("catalog.empty", "No candidates match your filters.")}</div>
      ) : (
        <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filtered.map((c) => (<CandidateCard key={c.id} candidate={c} />))}
        </div>
      )}
    </div>
  );
}
